import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { cn, formatCurrency, formatDate, formatDateTime } from '@/lib/utils';
import { Ticket, Trophy, LogOut, Home, Key, Calendar as CalendarIcon, User, ChevronLeft, ChevronRight, Eye, EyeOff, Layers, ShoppingBag, Sparkles, TrendingUp, Zap } from 'lucide-react';
import { toast } from '../lib/swal';
import ThemeToggle from '../components/ThemeToggle';
import ImpulsoCompra from '../components/ImpulsoCompra';
import StatCard from '../components/StatCard';
import AppButton from '../components/ui/AppButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UsuarioDashboard = () => {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Summary view state
  const [resumen, setResumen] = useState([]);
  const [loadingResumen, setLoadingResumen] = useState(true);

  // Detail view state (per-sorteo paginated)
  const [sorteoDetalle, setSorteoDetalle] = useState(null); // which sorteo is expanded
  const [boletosDetalle, setBoletosDetalle] = useState([]);
  const [detalleInfo, setDetalleInfo] = useState({ total: 0, page: 1, total_pages: 1, sorteo: null });
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('todos');

  // Premios state
  const [premiosGanados, setPremiosGanados] = useState([]);
  const [loadingPremios, setLoadingPremios] = useState(false);

  // Password change
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordActual, setPasswordActual] = useState('');
  const [passwordNueva, setPasswordNueva] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [showActual, setShowActual] = useState(false);
  const [showNueva, setShowNueva] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // New state for all active raffles (for impulse purchase)
  const [todosSorteos, setTodosSorteos] = useState([]);
  const [loadingTodosSorteos, setLoadingTodosSorteos] = useState(false);

  const tabInicial = searchParams.get('tab') === 'boletos' ? 'sorteos' : 'sorteos';
  const [activeTab, setActiveTab] = useState(tabInicial);

  // Celebration state
  const [isCelebrating, setIsCelebrating] = useState(false);
  const prevPremiosCount = useRef(0);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    // Si requiere cambio de contraseña, abrimos el modal
    if (user.requires_password_change) {
      setShowChangePassword(true);
    }

    fetchResumen();
    fetchMisPremios();
    fetchTodosSorteos();
  }, [user]);

  useEffect(() => {
    // Activar celebración si el número de premios aumenta
    if (!loadingPremios && premiosGanados.length > prevPremiosCount.current && premiosGanados.length > 0) {
      setIsCelebrating(true);
      const timer = setTimeout(() => setIsCelebrating(false), 30000); // 30 segundos
      return () => clearTimeout(timer);
    }
    prevPremiosCount.current = premiosGanados.length;
  }, [premiosGanados, loadingPremios]);

  const fetchTodosSorteos = async () => {
    setLoadingTodosSorteos(true);
    try {
      const response = await axios.get(`${API}/sorteos`);
      // Excluir completados y pausados si se quiere solo los que se pueden comprar
      const activos = response.data.filter(s => 
        (s.estado === 'published' || s.estado === 'activo') && !s.ventas_pausadas
      );
      setTodosSorteos(activos);
    } catch (error) {
      console.error('Error al cargar sorteos activos:', error);
    } finally {
      setLoadingTodosSorteos(false);
    }
  };

  const fetchResumen = async () => {
    setLoadingResumen(true);
    try {
      const response = await axios.get(`${API}/boletos/mis-boletos/resumen`);
      setResumen(response.data);
    } catch (error) {
      console.error('Error al cargar resumen:', error);
      toast.error('Error al cargar resumen de boletos');
    } finally {
      setLoadingResumen(false);
    }
  };

  const fetchBoletosDetalle = useCallback(async (sorteoId, page = 1, estado = 'todos') => {
    setLoadingDetalle(true);
    try {
      const params = new URLSearchParams({ page, limit: 15, estado });
      const response = await axios.get(
        `${API}/boletos/mis-boletos/sorteo/${sorteoId}?${params}`);
      setBoletosDetalle(response.data.boletos);
      setDetalleInfo({
        total: response.data.total,
        page: response.data.page,
        total_pages: response.data.total_pages,
        sorteo: response.data.sorteo
      });
    } catch (error) {
      console.error('Error al cargar boletos:', error);
      toast.error('Error al cargar boletos del sorteo');
    } finally {
      setLoadingDetalle(false);
    }
  }, []);

  const fetchMisPremios = async () => {
    setLoadingPremios(true);
    try {
      const response = await axios.get(`${API}/usuario/mis-premios`);
      setPremiosGanados(response.data);
    } catch (error) {
      console.error('Error al cargar premios ganados:', error);
    } finally {
      setLoadingPremios(false);
    }
  };

  const handleVerBoletos = (sorteoId) => {
    setSorteoDetalle(sorteoId);
    setFiltroEstado('todos');
    fetchBoletosDetalle(sorteoId, 1, 'todos');
  };

  const handleVolverResumen = () => {
    setSorteoDetalle(null);
    setBoletosDetalle([]);
    setDetalleInfo({ total: 0, page: 1, total_pages: 1, sorteo: null });
  };

  const handlePageChange = (newPage) => {
    fetchBoletosDetalle(sorteoDetalle, newPage, filtroEstado);
  };

  const handleEstadoChange = (nuevoEstado) => {
    setFiltroEstado(nuevoEstado);
    fetchBoletosDetalle(sorteoDetalle, 1, nuevoEstado);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!user?.requires_password_change && passwordNueva !== passwordConfirm) {
      toast.error('Las contrasenas no coinciden');
      return;
    }
    if (passwordNueva.length < 6) {
      toast.error('La contrasena debe tener al menos 6 caracteres');
      return;
    }
    setChangingPassword(true);
    try {
      await axios.put(
        `${API}/auth/cambiar-password?password_actual=${passwordActual}&password_nueva=${passwordNueva}`,
        {});
      toast.success('Contrasena cambiada exitosamente');
      
      // Actualizar el estado del usuario para quitar la bandera requires_password_change
      if (typeof checkAuth === 'function') {
        await checkAuth();
      }

      setShowChangePassword(false);
      setPasswordActual('');
      setPasswordNueva('');
      setPasswordConfirm('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar contrasena');
    } finally {
      setChangingPassword(false);
    }
  };

  // Calculate totals from resumen
  const totalBoletos = resumen.reduce((sum, r) => sum + r.total, 0);
  const totalActivos = resumen.reduce((sum, r) => sum + r.activos, 0);
  const totalPendientes = resumen.reduce((sum, r) => sum + r.pendientes, 0);

  return (
    <div className="min-h-screen gradient-background relative overflow-x-hidden">
      
      {/* 🎈 CELEBRATION OVERLAY (Balloons & Confetti) */}
      {isCelebrating && (
        <div className="fixed inset-0 z-[100] pointer-events-none overflow-hidden">
          {/* Balloons */}
          {[...Array(12)].map((_, i) => (
            <div 
              key={`balloon-${i}`}
              className="absolute bottom-0 animate-float-up"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${6 + Math.random() * 4}s`,
                opacity: 0.7
              }}
            >
              <div className="w-12 h-16 rounded-full shadow-xl relative"
                style={{ backgroundColor: ['#ef4444', '#3b82f6', '#eab308', '#22c55e', '#a855f7'][i % 5] }}
              >
                <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-0.5 h-6 bg-slate-300/30" />
                <div className="absolute top-2 left-3 w-3 h-4 bg-white/30 rounded-full blur-[2px]" />
              </div>
            </div>
          ))}

          {/* Confetti */}
          {[...Array(40)].map((_, i) => (
            <div 
              key={`confetti-${i}`}
              className="absolute -top-10 w-2 h-4 animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                backgroundColor: ['#f43f5e', '#3b82f6', '#fbbf24', '#22c55e', '#c084fc', '#60a5fa'][i % 6],
                animationDelay: `${Math.random() * 10}s`,
                animationDuration: `${3 + Math.random() * 3}s`,
                transform: `rotate(${Math.random() * 360}deg)`
              }}
            />
          ))}
        </div>
      )}
      {/* Header */}
      <div className="bg-card shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-bold text-xl shadow-inner">
                {user?.name?.[0]?.toUpperCase() || 'U'}
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-foreground">Mi Panel</h1>
                <p className="text-sm text-muted-foreground truncate max-w-[200px] md:max-w-none">{user?.email}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 w-full md:w-auto items-center">
              <ThemeToggle />
              <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
                <DialogTrigger asChild>
                  <button className="flex-1 md:flex-none px-4 py-2 bg-white/50 dark:bg-white/5 hover:bg-white dark:hover:bg-white/10 text-foreground border border-white/20 backdrop-blur-md rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2">
                    <Key className="w-4 h-4" />
                    <span className="hidden sm:inline">Cambiar Contraseña</span>
                    <span className="sm:hidden">Contraseña</span>
                  </button>
                </DialogTrigger>
                <DialogContent 
                  hideCloseButton={user?.requires_password_change}
                  className="max-w-md bg-white/95 dark:bg-card/70 backdrop-blur-2xl border-black/5 dark:border-white/10 shadow-2xl rounded-[2rem] pb-8 overflow-hidden"
                  onPointerDownOutside={(e) => {
                    // Prevenir cerrar haciendo clic afuera si requiere cambio de clave
                    if (user?.requires_password_change) {
                      e.preventDefault();
                    }
                  }}
                  onInteractOutside={(e) => {
                    if (user?.requires_password_change) {
                      e.preventDefault();
                    }
                  }}
                >
                  {/* Decorative background for light mode */}
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary/50 via-primary to-primary/50" />
                  
                  <DialogHeader className="border-b border-black/5 dark:border-white/5 pb-6 pt-2">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-2xl bg-primary/10 border border-primary/20 shadow-inner">
                        <Key className="w-5 h-5 text-primary" />
                      </div>
                      <DialogTitle className="text-xl font-black uppercase tracking-tight text-foreground">
                        {user?.requires_password_change ? "Establecer Contraseña" : "Cambiar Contraseña"}
                      </DialogTitle>
                    </div>
                    {user?.requires_password_change && (
                      <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                        ¡Felicidades por tu compra! 🎉<br/>
                        Por favor, asigna una contraseña segura para tu cuenta. Esto te permitirá acceder a tus boletos y premios en el futuro.
                      </p>
                    )}
                  </DialogHeader>
                  
                  <form onSubmit={handleChangePassword} className="space-y-6 pt-6">
                    {/* Contraseña Actual (Ocultar si es usuario nuevo) */}
                    {!user?.requires_password_change && (
                      <div className="space-y-2">
                        <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Contraseña Actual</Label>
                        <div className="relative group">
                          <Input 
                            id="password-actual" 
                            type={showActual ? "text" : "password"} 
                            value={passwordActual} 
                            onChange={(e) => setPasswordActual(e.target.value)} 
                            required 
                            className="h-14 rounded-2xl bg-black/[0.03] dark:bg-white/5 border-black/5 dark:border-white/10 pr-12 focus:ring-primary/20 transition-all font-medium" 
                          />
                          <button
                            type="button"
                            onClick={() => setShowActual(!showActual)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-all duration-300"
                          >
                            {showActual ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Nueva Contraseña */}
                    <div className="space-y-2">
                      <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Nueva Contraseña</Label>
                      <div className="relative group">
                        <Input 
                          id="password-nueva" 
                          type={showNueva ? "text" : "password"} 
                          value={passwordNueva} 
                          onChange={(e) => setPasswordNueva(e.target.value)} 
                          required 
                          className="h-14 rounded-2xl bg-black/[0.03] dark:bg-white/5 border-black/5 dark:border-white/10 pr-12 focus:ring-primary/20 transition-all font-medium" 
                        />
                        <button
                          type="button"
                          onClick={() => setShowNueva(!showNueva)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-all duration-300"
                        >
                          {showNueva ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    {/* Confirmar Nueva Contraseña (Ocultar si es usuario nuevo) */}
                    {!user?.requires_password_change && (
                      <div className="space-y-2">
                        <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Confirmar Nueva Contraseña</Label>
                        <div className="relative group">
                          <Input 
                            id="password-confirm" 
                            type={showConfirm ? "text" : "password"} 
                            value={passwordConfirm} 
                            onChange={(e) => setPasswordConfirm(e.target.value)} 
                            required={!user?.requires_password_change} 
                            className="h-14 rounded-2xl bg-black/[0.03] dark:bg-white/5 border-black/5 dark:border-white/10 pr-12 focus:ring-primary/20 transition-all font-medium" 
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirm(!showConfirm)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-all duration-300"
                          >
                            {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="flex gap-3 justify-end pt-4">
                      {!user?.requires_password_change && (
                        <AppButton 
                          type="button" 
                          variant="ghost" 
                          onClick={() => setShowChangePassword(false)}
                          className="font-bold"
                        >
                          Cancelar
                        </AppButton>
                      )}
                      <AppButton 
                        type="submit" 
                        variant="primary"
                        loading={changingPassword}
                        className="px-8 shadow-xl shadow-primary/20"
                      >
                        {user?.requires_password_change ? "Guardar Contraseña" : "Actualizar Contraseña"}
                      </AppButton>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
              
              <AppButton 
                variant="secondary" 
                size="sm"
                onClick={() => navigate('/usuario/perfil')} 
                icon={User}
                className="flex-1 md:flex-none"
              >
                Mi Perfil
              </AppButton>
              
              <AppButton 
                variant="secondary" 
                size="sm"
                onClick={() => navigate('/')} 
                icon={Home}
                className="flex-1 md:flex-none"
              >
                Inicio
              </AppButton>
              
              <AppButton 
                variant="danger" 
                size="sm"
                onClick={handleLogout} 
                icon={LogOut}
                className="flex-1 md:flex-none"
              >
                Salir
              </AppButton>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Winner banner REDISEÑADO - IMPACTANTE */}
        {!loadingPremios && premiosGanados.length > 0 && (
          <div className="relative mb-12 animate-in zoom-in slide-in-from-top-12 duration-1000">
            {/* Glow exterior */}
            <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-600 rounded-[3rem] blur opacity-30 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-pulse" />
            
            <Card className="relative bg-white/80 dark:bg-slate-900/60 backdrop-blur-3xl border-none overflow-hidden rounded-[3rem] shadow-2xl ring-1 ring-yellow-500/20" data-testid="winner-banner">
              <CardContent className="p-0">
                <div className="flex flex-col md:flex-row items-stretch">
                  {/* Left Column: Trophy & Badge */}
                  <div className="w-full md:w-64 bg-gradient-to-br from-yellow-400 via-amber-500 to-yellow-600 flex flex-col items-center justify-center p-10 text-white relative overflow-hidden">
                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10" />
                    <Sparkles className="absolute top-4 left-4 w-6 h-6 text-yellow-200 animate-pulse" />
                    <Sparkles className="absolute bottom-4 right-4 w-6 h-6 text-white/40 animate-pulse delay-500" />
                    
                    <div className="relative z-10 w-24 h-24 bg-white/20 rounded-full flex items-center justify-center shadow-2xl border-4 border-white/30 mb-4 animate-bounce-slow">
                      <Trophy className="w-12 h-12 text-white drop-shadow-lg" />
                    </div>
                    
                    <div className="relative z-10 text-center">
                      <p className="text-[10px] font-black uppercase tracking-[0.4em] text-white/80 mb-1">Status</p>
                      <h4 className="text-xl font-black italic tracking-tighter uppercase">GANADOR</h4>
                    </div>
                  </div>

                  {/* Right Column: Info & Action */}
                  <div className="flex-1 p-8 md:p-12 flex flex-col justify-center items-start text-left bg-gradient-to-r from-transparent to-yellow-500/5">
                    <div className="space-y-4 w-full">
                       <Badge className="bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/30 font-black text-[10px] tracking-[0.3em] uppercase px-4 py-1.5 rounded-xl">
                         NUEVO PREMIO DETECTADO
                       </Badge>
                       
                       <div className="space-y-2">
                         <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-foreground italic tracking-tighter uppercase leading-[0.9]">
                           ¡FELICIDADES, <span className="text-yellow-600 dark:text-yellow-500">{user?.name?.split(' ')[0] || 'CAMPEÓN'}</span>!
                         </h2>
                         <p className="text-lg md:text-xl text-muted-foreground font-medium max-w-2xl leading-relaxed">
                            Tu suerte ha dado frutos. Posees <span className="text-foreground font-black italic underline decoration-yellow-500 decoration-4">{premiosGanados.length} premio{premiosGanados.length !== 1 ? 's' : ''}</span> esperando por ti en tu colección privada.
                         </p>
                       </div>

                       <div className="pt-6 flex flex-wrap gap-4">
                          <AppButton 
                            variant="primary" 
                            className="h-14 px-10 rounded-2xl bg-yellow-600 hover:bg-yellow-500 text-white font-black italic uppercase tracking-widest shadow-xl shadow-yellow-600/30 border-none"
                            onClick={() => setActiveTab('premios')}
                          >
                             RECLAMAR PREMIOS
                          </AppButton>
                          <div className="flex items-center gap-3 px-6 h-14 bg-black/5 dark:bg-white/5 rounded-2xl border border-black/5 dark:border-white/10">
                             <div className="w-2 h-2 bg-yellow-500 rounded-full animate-ping" />
                             <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">
                               Actualizado hace unos instantes
                             </span>
                          </div>
                       </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          <StatCard 
            title="Total Boletos" 
            value={totalBoletos} 
            icon={Ticket} 
            color="orange" 
          />
          
          <StatCard 
            title="Boletos Activos" 
            value={totalActivos} 
            icon={Ticket} 
            color="blue" 
          />

          <StatCard 
            title="Premios Ganados" 
            value={loadingPremios ? 0 : premiosGanados.length} 
            icon={Trophy} 
            color="gold" 
          />
        </div>

        {/* SECTION: IMPULSO DE COMPRA (PACKAGES) */}
        {!loadingTodosSorteos && (
          <ImpulsoCompra sorteos={todosSorteos} />
        )}

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-2 h-auto">
            <TabsTrigger value="sorteos" data-testid="tab-sorteos" className="text-xs sm:text-sm py-2">
              <Layers className="w-4 h-4 mr-1" />
              Mis Sorteos
            </TabsTrigger>
            <TabsTrigger value="premios" data-testid="tab-premios" className="text-xs sm:text-sm py-2">
              <Trophy className="w-4 h-4 mr-1" />
              Premios Ganados
            </TabsTrigger>
          </TabsList>

          {/* TAB: MIS SORTEOS */}
          <TabsContent value="sorteos" className="space-y-4">
            {loadingResumen ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                <p className="mt-4 text-gray-600">Cargando tus sorteos...</p>
              </div>
            ) : sorteoDetalle ? (
              /* DETAIL VIEW - Paginated tickets for a specific sorteo */
              <DetailView
                detalleInfo={detalleInfo}
                boletosDetalle={boletosDetalle}
                loadingDetalle={loadingDetalle}
                filtroEstado={filtroEstado}
                onVolverResumen={handleVolverResumen}
                onPageChange={handlePageChange}
                onEstadoChange={handleEstadoChange}
              />
            ) : resumen.length === 0 ? (
              <div className="py-12 animate-in fade-in zoom-in duration-500">
                <Card className="max-w-2xl mx-auto p-12 text-center border-0 shadow-2xl bg-white/40 dark:bg-white/5 backdrop-blur-xl relative overflow-hidden group rounded-[3rem]" data-testid="no-sorteos-message">
                  {/* Decorative Glow */}
                  <div className="absolute -top-24 -left-24 w-64 h-64 bg-primary/20 rounded-full blur-[100px] pointer-events-none" />
                  
                  <div className="relative z-10 flex flex-col items-center">
                    <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center mb-6 border border-primary/20">
                      <ShoppingBag className="w-12 h-12 text-primary animate-bounce-slow" />
                    </div>
                    
                    <h3 className="text-3xl font-black uppercase tracking-tighter mb-4 text-foreground">
                      Aún no participas en ningún sorteo
                    </h3>
                    
                    <p className="text-muted-foreground font-medium text-lg mb-8 max-w-md mx-auto">
                      ¡No dejes pasar la oportunidad! Compra tus boletos ahora y empieza a ganar premios increíbles.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-4 w-full justify-center">
                      <AppButton 
                        variant="primary"
                        size="lg"
                        onClick={() => navigate('/')} 
                        icon={Layers}
                        className="px-10"
                        data-testid="ver-sorteos-btn"
                      >
                        Ver sorteos disponibles
                      </AppButton>
                      
                      <AppButton 
                        variant="secondary"
                        size="lg"
                        onClick={() => navigate('/')} 
                        icon={ShoppingBag}
                        className="px-10"
                      >
                        Comprar ahora
                      </AppButton>
                    </div>

                    <div className="mt-12 pt-8 border-t border-white/10 w-full flex items-center justify-center gap-8 opacity-50">
                      <div className="flex flex-col items-center">
                        <TrendingUp className="w-5 h-5 mb-1" />
                        <span className="text-[10px] font-black uppercase">Más Probabilidades</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <Trophy className="w-5 h-5 mb-1" />
                        <span className="text-[10px] font-black uppercase">Premios Premium</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <Zap className="w-5 h-5 mb-1" />
                        <span className="text-[10px] font-black uppercase">Sorteos en Vivo</span>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            ) : (
              /* SUMMARY VIEW - Cards grouped by raffle */
              <div className="grid gap-6" data-testid="resumen-sorteos">
                {resumen.map((item) => (
                  <Card 
                    key={item.sorteo_id} 
                    className="group overflow-hidden border-none shadow-xl bg-card/50 backdrop-blur-xl transition-all duration-300 hover:shadow-2xl hover:bg-card/70" 
                    data-testid={`sorteo-card-${item.sorteo_id}`}
                  >
                    <div className="flex flex-col md:flex-row">
                      {/* Raffle Image Thumbnail */}
                      <div className="w-full md:w-48 h-48 md:h-auto relative overflow-hidden bg-slate-100 dark:bg-white/5">
                        {item.sorteo_imagen ? (
                          <img 
                            src={item.sorteo_imagen} 
                            alt={item.sorteo_titulo}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/300x300?text=WishWay';
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-primary/5">
                            <Layers className="w-12 h-12 text-primary/20" />
                          </div>
                        )}
                        <div className="absolute top-3 left-3">
                          <Badge className={cn(
                            "shadow-lg backdrop-blur-md border-none px-3 py-1 font-black text-[10px] uppercase tracking-wider",
                            item.sorteo_estado === 'published' || item.sorteo_estado === 'activo' ? "bg-green-500 text-white" :
                            item.sorteo_estado === 'live' ? "bg-red-500 animate-pulse text-white" :
                            "bg-slate-500 text-white"
                          )}>
                            {item.sorteo_estado === 'published' || item.sorteo_estado === 'activo' ? 'Publicado' :
                             item.sorteo_estado === 'waiting' ? 'En espera' :
                             item.sorteo_estado === 'live' ? 'EN VIVO' :
                             item.sorteo_estado === 'completed' ? 'Completado' :
                             item.sorteo_estado.toUpperCase()}
                          </Badge>
                        </div>
                      </div>

                      <div className="flex-1 p-6 md:p-8 flex flex-col justify-between">
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                          <div className="space-y-3">
                            <h3 className="text-xl md:text-2xl font-black tracking-tight text-foreground group-hover:text-primary transition-colors">
                              {item.sorteo_titulo}
                            </h3>
                            
                            <div className="flex flex-wrap items-center gap-4">
                              <div className="flex items-center gap-2 bg-slate-100 dark:bg-white/5 px-3 py-1.5 rounded-xl border border-slate-200 dark:border-white/10">
                                <Ticket className="w-4 h-4 text-primary" />
                                <span className="text-sm font-bold">{item.total} boletos comprados</span>
                              </div>
                              
                              <div className="flex items-center gap-3">
                                <div className="flex items-center gap-1.5">
                                  <div className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]"></div>
                                  <span className="text-xs font-black uppercase tracking-widest text-muted-foreground">Activos: {item.activos}</span>
                                </div>
                                {item.pendientes > 0 && (
                                  <div className="flex items-center gap-1.5">
                                    <div className="w-2 h-2 rounded-full bg-yellow-500 shadow-[0_0_8px_rgba(234,179,8,0.6)]"></div>
                                    <span className="text-xs font-black uppercase tracking-widest text-muted-foreground">Pendientes: {item.pendientes}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-col sm:flex-row gap-3">
                            <AppButton
                              variant="secondary"
                              size="sm"
                              onClick={() => navigate(`/sorteo/${item.landing_slug || item.sorteo_id}`)}
                              icon={ShoppingBag}
                              className="h-11 px-6 rounded-xl font-black text-[10px] uppercase tracking-widest"
                            >
                              Comprar más
                            </AppButton>
                            
                            <AppButton
                              variant="primary"
                              size="sm"
                              onClick={() => handleVerBoletos(item.sorteo_id)}
                              icon={Eye}
                              className="h-11 px-6 rounded-xl font-black text-[10px] uppercase tracking-widest"
                            >
                              Ver boletos
                            </AppButton>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* TAB: PREMIOS GANADOS */}
          <TabsContent value="premios" className="space-y-4">
            {loadingPremios ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                <p className="mt-4 text-gray-600">Cargando premios ganados...</p>
              </div>
            ) : premiosGanados.length === 0 ? (
              <Card className="p-12 text-center" data-testid="no-premios-message">
                <Trophy className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <h3 className="text-xl font-semibold mb-2">Aun no has ganado premios</h3>
                <p className="text-gray-600">Sigue participando y buena suerte!</p>
              </Card>
            ) : (
              <div className="grid gap-6" data-testid="premios-list">
                {premiosGanados.map((premio, index) => (
                  <Card 
                    key={premio.id || index} 
                    className="group relative overflow-hidden border-none shadow-xl bg-gradient-to-br from-amber-500/10 to-yellow-500/5 backdrop-blur-xl border border-yellow-500/20"
                  >
                    {/* Golden Glow Effect */}
                    <div className="absolute -top-24 -right-24 w-48 h-48 bg-yellow-500/20 blur-[80px] rounded-full group-hover:bg-yellow-500/30 transition-all duration-500"></div>
                    
                    <CardContent className="p-6 md:p-8">
                      <div className="flex flex-col md:flex-row items-center gap-8 text-center md:text-left">
                        {/* Trophy/Image Column */}
                        <div className="relative">
                          {(premio.premio?.imagen || premio.sorteo?.sorteo_imagen) ? (
                            <div className="w-32 h-32 md:w-40 md:h-40 rounded-3xl overflow-hidden shadow-2xl border-4 border-yellow-500/30 relative z-10">
                              <img
                                src={premio.premio?.imagen || premio.sorteo?.sorteo_imagen}
                                alt={premio.premio?.nombre || 'Premio'}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                              />
                            </div>
                          ) : (
                            <div className="w-32 h-32 md:w-40 md:h-40 rounded-3xl bg-gradient-to-br from-yellow-400 to-amber-600 flex items-center justify-center shadow-2xl border-4 border-yellow-500/30 relative z-10">
                              <Trophy className="w-16 h-16 text-white drop-shadow-lg" />
                            </div>
                          )}
                          <div className="absolute -bottom-2 -right-2 bg-yellow-500 text-white p-2 rounded-xl shadow-lg z-20 animate-bounce">
                            <Sparkles className="w-5 h-5" />
                          </div>
                        </div>

                        {/* Title and Info Column */}
                        <div className="flex-1 space-y-4">
                          <div className="space-y-1">
                            <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-2">
                              <Badge className="bg-yellow-500 text-white font-black text-[10px] uppercase tracking-widest px-3 py-1">Ganador!</Badge>
                              {premio.premio?.etapa_numero && (
                                <Badge variant="outline" className="border-yellow-500/30 text-yellow-600 dark:text-yellow-400 font-bold text-[10px] uppercase">Etapa {premio.premio.etapa_numero}</Badge>
                              )}
                            </div>
                            <h3 className="text-2xl md:text-3xl font-black tracking-tight text-foreground line-clamp-2">
                              {premio.sorteo?.titulo || 'Sorteo'}
                            </h3>
                          </div>

                          <div className="p-4 rounded-2xl bg-white/50 dark:bg-white/5 border border-yellow-500/10 inline-block w-full sm:w-auto">
                            <p className="text-[10px] font-black uppercase tracking-widest text-yellow-600 dark:text-yellow-500 mb-1">Tu Premio:</p>
                            <p className="text-xl font-bold text-foreground">{premio.premio?.nombre || 'Premio Principal'}</p>
                          </div>

                          <div className="flex flex-wrap justify-center md:justify-start items-center gap-6">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                                <Ticket className="w-4 h-4 text-yellow-600 dark:text-yellow-500" />
                              </div>
                              <span className="text-sm font-bold">Boleto #{premio.numero_boleto}</span>
                            </div>
                            
                            {premio.fecha_sorteo && (
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                                  <CalendarIcon className="w-4 h-4 text-yellow-600 dark:text-yellow-500" />
                                </div>
                                <span className="text-sm font-bold">{formatDate(premio.fecha_sorteo)}</span>
                              </div>
                            )}
                          </div>

                          <div className="pt-2">
                            {premio.sorteo?.landing_slug && (
                              <AppButton 
                                variant="secondary" 
                                size="sm" 
                                onClick={() => navigate(`/sorteo/${premio.sorteo.landing_slug}`)}
                                icon={Eye}
                              >
                                Ver Sorteo
                              </AppButton>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

/* ---- Detail View Component ---- */
const DetailView = ({ detalleInfo, boletosDetalle, loadingDetalle, filtroEstado, onVolverResumen, onPageChange, onEstadoChange }) => {
  return (
    <div className="space-y-6" data-testid="detalle-view">
      {/* Back button + Title */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-card/50 dark:bg-card/5 p-4 rounded-3xl border border-white/10 backdrop-blur-md">
        <div className="flex items-center gap-4">
          <AppButton 
            variant="ghost" 
            size="sm" 
            onClick={onVolverResumen} 
            icon={ChevronLeft}
            className="rounded-full"
            data-testid="volver-resumen-btn"
          >
            Volver
          </AppButton>
          <div>
            <h2 className="text-xl font-black text-foreground">{detalleInfo.sorteo?.titulo || 'Sorteo'}</h2>
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest">
              {detalleInfo.total} boleto{detalleInfo.total !== 1 ? 's' : ''} comprados
            </p>
          </div>
        </div>

        {/* Estado filter */}
        <div className="flex p-1.5 bg-black/5 dark:bg-black/40 rounded-2xl border border-white/10 backdrop-blur-md">
          {['todos', 'activos', 'pendientes'].map((estado) => (
            <AppButton
              key={estado}
              variant={filtroEstado === estado ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => onEstadoChange(estado)}
              className={cn(
                "rounded-xl font-black text-[10px] uppercase transition-all duration-300",
                filtroEstado === estado ? "shadow-lg shadow-primary/20" : "text-muted-foreground"
              )}
              data-testid={`filtro-${estado}-btn`}
            >
              {estado === 'todos' ? 'Todos' : estado === 'activos' ? 'Activos' : 'Pendientes'}
            </AppButton>
          ))}
        </div>
      </div>

      {/* Tickets list */}
      {loadingDetalle ? (
        <div className="text-center py-24">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-sm font-bold text-muted-foreground uppercase tracking-widest">Sincronizando tus boletos...</p>
        </div>
      ) : boletosDetalle.length === 0 ? (
        <Card className="p-16 border-dashed border-2 bg-transparent text-center" data-testid="no-boletos-detalle">
          <Ticket className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-20" />
          <p className="text-muted-foreground font-bold uppercase tracking-widest">No se encontraron boletos</p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="boletos-list">
            {boletosDetalle.map((boleto) => (
              <div 
                key={boleto.id} 
                className={`relative group overflow-hidden rounded-3xl border transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl ${
                  boleto.pago_confirmado 
                  ? 'bg-white dark:bg-white/5 border-gray-100 dark:border-white/10' 
                  : 'bg-orange-50/50 dark:bg-orange-500/10 border-orange-200 dark:border-orange-500/20'
                }`}
                data-testid={`boleto-card-${boleto.numero_boleto}`}
              >
                {/* Ticket Aesthetic Side Strip */}
                <div className={`absolute left-0 top-0 bottom-0 w-2 ${
                  boleto.pago_confirmado ? 'bg-green-500' : 'bg-orange-500'
                }`} />

                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex flex-col">
                      <span className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em] mb-1">Boleto No.</span>
                      <span className="text-4xl font-black text-foreground tabular-nums tracking-tighter">
                        #{boleto.numero_boleto}
                      </span>
                    </div>
                    <Badge className={`px-3 py-1 font-black uppercase text-[10px] tracking-widest rounded-full ${
                      boleto.pago_confirmado 
                      ? 'bg-green-500/10 text-green-600 dark:text-green-400 border-none' 
                      : 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-none animate-pulse'
                    }`}>
                      {boleto.pago_confirmado ? 'Activo' : 'Pendiente'}
                    </Badge>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-black/20 rounded-2xl border border-gray-100 dark:border-white/5">
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          <Ticket className="w-4 h-4 text-primary" />
                        </div>
                        <span className="text-sm font-black text-foreground">{formatCurrency(boleto.precio_pagado)}</span>
                      </div>
                      <span className="text-[10px] font-bold text-muted-foreground uppercase">{formatDateTime(boleto.fecha_compra)}</span>
                    </div>

                    {boleto.numero_comprobante && (
                      <div className="flex items-center gap-2 px-1">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                          Ref: <span className="text-foreground">{boleto.numero_comprobante}</span>
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Perforation holes visual effect */}
                  <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-card rounded-full border-r border-gray-100 dark:border-white/10 hidden group-hover:block" />
                  <div className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-card rounded-full border-l border-gray-100 dark:border-white/10 hidden group-hover:block" />
                </CardContent>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {detalleInfo.total_pages > 1 && (
            <div className="flex items-center justify-center gap-4 py-8" data-testid="pagination-controls">
              <Button
                variant="outline"
                size="icon"
                className="rounded-full w-12 h-12"
                disabled={detalleInfo.page <= 1}
                onClick={() => onPageChange(detalleInfo.page - 1)}
                data-testid="prev-page-btn"
              >
                <ChevronLeft className="w-6 h-6" />
              </Button>
              
              <div className="flex items-center gap-2 px-6 py-3 bg-white/50 dark:bg-white/5 rounded-full border border-white/20 backdrop-blur-md">
                <span className="text-xs font-black uppercase tracking-widest text-muted-foreground">Página</span>
                <span className="text-sm font-black text-primary">{detalleInfo.page}</span>
                <span className="text-xs font-black uppercase tracking-widest text-muted-foreground">de</span>
                <span className="text-sm font-black text-foreground">{detalleInfo.total_pages}</span>
              </div>

              <Button
                variant="outline"
                size="icon"
                className="rounded-full w-12 h-12"
                disabled={detalleInfo.page >= detalleInfo.total_pages}
                onClick={() => onPageChange(detalleInfo.page + 1)}
                data-testid="next-page-btn"
              >
                <ChevronRight className="w-6 h-6" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UsuarioDashboard;
